/**
 * ThemeProvider — React context that manages theme selection, mode toggling,
 * and theme loading from BFF + built-in themes.
 *
 * Requirements: 2.1-2.5, 2.11, 2.13, 2.14
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ThemeDefinition,
  ThemeMode,
  ResolvedMode,
} from './types';
import {
  ThemeRegistry,
  getPersistedMode,
  persistMode,
  resolveMode,
  applyMode,
  cycleMode,
  fetchThemes,
  loadThemeById,
  getPersistedThemeId,
  persistThemeId,
} from './theme-engine';
import { devTechnologyTheme } from './themes/dev-technology';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface ThemeContextValue {
  readonly mode: ThemeMode;
  readonly resolvedMode: ResolvedMode;
  readonly activeThemeId: string;
  readonly themes: ThemeDefinition[];
  toggle: () => void;
  switchTheme: (id: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ---------------------------------------------------------------------------
// Default theme ID
// ---------------------------------------------------------------------------

const DEFAULT_THEME_ID = devTechnologyTheme.id;

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ThemeProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [mode, setMode] = useState<ThemeMode>(getPersistedMode);
  const [resolved, setResolved] = useState<ResolvedMode>(() => resolveMode(mode));
  const [themes, setThemes] = useState<ThemeDefinition[]>([devTechnologyTheme]);
  const [activeThemeId, setActiveThemeId] = useState<string>(() => {
    return getPersistedThemeId() ?? DEFAULT_THEME_ID;
  });

  // Build registry from themes list
  const registry: ThemeRegistry = useMemo(() => {
    const themesMap = new Map<string, ThemeDefinition>();
    for (const theme of themes) {
      themesMap.set(theme.id, theme);
    }
    return {
      themes: themesMap,
      activeThemeId,
      systemThemeId: DEFAULT_THEME_ID,
    };
  }, [themes, activeThemeId]);

  // ---------------------------------------------------------------------------
  // Fetch themes from BFF on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    async function loadThemes(): Promise<void> {
      const fetched = await fetchThemes();
      if (cancelled) return;

      // Merge fetched themes with built-in, avoiding duplicates
      const merged = [devTechnologyTheme, ...fetched.filter((t) => t.id !== devTechnologyTheme.id)];
      setThemes(merged);
    }

    loadThemes();
    return () => { cancelled = true; };
  }, []);

  // ---------------------------------------------------------------------------
  // Apply mode and theme on changes
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const r = resolveMode(mode);
    setResolved(r);
    applyMode(r);
    persistMode(mode);
  }, [mode]);

  // Apply active theme when mode, theme, or registry changes
  useEffect(() => {
    // Validate persisted theme ID — fall back to default if invalid
    if (!registry.themes.has(activeThemeId)) {
      setActiveThemeId(DEFAULT_THEME_ID);
      return;
    }
    loadThemeById(activeThemeId, registry, resolved);
  }, [activeThemeId, registry, resolved]);

  // Listen for OS preference changes when mode is 'system'
  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      const r: ResolvedMode = e.matches ? 'dark' : 'light';
      setResolved(r);
      applyMode(r);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const toggle = useCallback(() => {
    setMode((prev) => cycleMode(prev));
  }, []);

  const switchTheme = useCallback((id: string) => {
    setActiveThemeId(id);
    persistThemeId(id);
  }, []);

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------

  const value: ThemeContextValue = useMemo(() => ({
    mode,
    resolvedMode: resolved,
    activeThemeId,
    themes,
    toggle,
    switchTheme,
  }), [mode, resolved, activeThemeId, themes, toggle, switchTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
